import React, { useState } from "react";
import { Headphones, Loader2, Play, Video } from "lucide-react";

function isEmbeddableUrl(url = "") {
  return /mediadelivery\.net|youtube\.com|youtu\.be|vimeo\.com|iframe/i.test(url);
}

export default function MediaEmbedPlayer({ item, color, variant = "video" }) {
  const [playing, setPlaying] = useState(false);
  const isPodcast = variant === "podcast";
  const Icon = isPodcast ? Headphones : Video;
  const canEmbed = Boolean(item?.mediaUrl) && isEmbeddableUrl(item.mediaUrl);

  if (!canEmbed) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-2xl bg-neutral-950 text-sm font-semibold text-white/70">
        Playback unavailable
      </div>
    );
  }

  if (!playing) {
    return (
      <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-neutral-950">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{ background: `radial-gradient(circle at 30% 20%, ${color}, transparent 55%)` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/55" aria-hidden />
        <div className="absolute left-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/45 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-white backdrop-blur-md">
          <Icon size={12} strokeWidth={2.25} style={{ color }} aria-hidden />
          {isPodcast ? "Podcast" : "Video"}
        </div>
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="absolute left-1/2 top-1/2 z-10 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/55 bg-white text-neutral-950 shadow-xl transition hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:h-16 sm:w-16"
          style={{ outlineColor: color }}
          aria-label={`Play ${item.title}`}
        >
          <Play size={22} strokeWidth={0} fill="currentColor" className="ml-0.5" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 z-10 px-4 pb-4 pt-10">
          <p className="line-clamp-2 text-left text-sm font-black text-white">{item.title}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black ring-1 ring-black/10">
      <iframe
        src={item.mediaUrl}
        title={item.title}
        className="absolute inset-0 h-full w-full border-0"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        loading="lazy"
      />
      <div className="pointer-events-none absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-white backdrop-blur-sm">
        <Loader2 size={10} className="animate-spin opacity-80" aria-hidden />
        Now playing
      </div>
    </div>
  );
}
